import { useCallback, useMemo } from 'react';
import { useLocation, useSearch } from 'wouter';

export interface ServiceSelectorFilters {
  term?: string;
  jurisdiction?: string;
  strictJurisdiction?: boolean;
  currency?: string;
  type?: string;
  minPrice?: number;
  maxPrice?: number;
  serviceFilter?: 'packages' | 'standalone';
}

const PARAM_KEYS = {
  term: 'svc_term',
  jurisdiction: 'svc_jur',
  strictJurisdiction: 'svc_jurStrict',
  currency: 'svc_ccy',
  type: 'svc_type',
  minPrice: 'svc_min',
  maxPrice: 'svc_max',
  serviceFilter: 'svc_pkg',
} as const;

export function useServiceSelectorFilters() {
  const [location, setLocation] = useLocation();
  const search = useSearch();

  const filters = useMemo((): ServiceSelectorFilters => {
    const p = new URLSearchParams(search);
    const result: ServiceSelectorFilters = {};

    const term = p.get(PARAM_KEYS.term);
    if (term) result.term = term;

    const jurisdiction = p.get(PARAM_KEYS.jurisdiction);
    if (jurisdiction) result.jurisdiction = jurisdiction;

    const strict = p.get(PARAM_KEYS.strictJurisdiction);
    if (strict === '1' || strict === 'true') result.strictJurisdiction = true;

    const currency = p.get(PARAM_KEYS.currency);
    if (currency) result.currency = currency;

    const type = p.get(PARAM_KEYS.type);
    if (type) result.type = type;

    const minPrice = p.get(PARAM_KEYS.minPrice);
    if (minPrice) result.minPrice = Number(minPrice);

    const maxPrice = p.get(PARAM_KEYS.maxPrice);
    if (maxPrice) result.maxPrice = Number(maxPrice);

    const sf = p.get(PARAM_KEYS.serviceFilter);
    if (sf === 'packages' || sf === 'standalone') result.serviceFilter = sf;

    return result;
  }, [search]);

  const writeParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(search);
      mutate(params);
      const qs = params.toString();
      setLocation(qs ? `${location}?${qs}` : location);
    },
    [search, location, setLocation],
  );

  const setFilter = useCallback(
    <K extends keyof ServiceSelectorFilters>(
      key: K,
      value: ServiceSelectorFilters[K],
    ) => {
      writeParams((params) => {
        const paramKey = PARAM_KEYS[key];
        if (
          value === undefined ||
          value === null ||
          value === '' ||
          value === false
        ) {
          params.delete(paramKey);
        } else if (typeof value === 'boolean') {
          params.set(paramKey, '1');
        } else {
          params.set(paramKey, String(value));
        }
      });
    },
    [writeParams],
  );

  const clearFilters = useCallback(() => {
    writeParams((params) => {
      for (const key of Object.values(PARAM_KEYS)) {
        params.delete(key);
      }
    });
  }, [writeParams]);

  return { filters, setFilter, clearFilters };
}
